import { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useInView, motion } from "framer-motion";
import { Lock, Eye, Compass, Puzzle, ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { Seo, legalServiceSchema } from "../lib/seo";
import { SITE, IMAGES, telLink } from "../lib/site";
import { fetchArticles } from "../lib/api";
import { MaskedLines, Reveal, Stagger, StaggerItem, useParallax } from "../components/motion";
import { Marquee } from "../components/Marquee";
import { SectionHeading, ArticleCard, ServiceRow } from "../components/bits";
import { SERVICES } from "../data/services";
import { INDUSTRIES } from "../data/industries";

const PILLARS = [
  { icon: Lock, title: "Confidentiality", d: "What you share with us stays with us. Discretion is the baseline of the practice, not a courtesy." },
  { icon: Compass, title: "Diligence", d: "Every matter is worked to the detail — the record checked, the clause read twice, the deadline diarised." },
  { icon: Eye, title: "Transparency", d: "Clear scope, clear fees, clear timelines. You will always know where your matter stands." },
  { icon: Puzzle, title: "Practical Solutions", d: "Advice framed as options you can act on — built around your commercial reality, not textbook theory." },
];

const HeroCard = () => (
  <div className="border border-gold/25 bg-navy-dark/85 p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)] backdrop-blur-xl">
    <p className="eyebrow-on-dark">Areas of practice</p>
    <ul className="mt-6 space-y-4">
      {SERVICES.map((s, i) => (
        <li key={s.slug} className="flex items-baseline gap-4">
          <span className="font-mono text-[10px] text-gold">{s.num}</span>
          <span className="text-sm text-white/80">{s.title}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Hero = () => {
  const { ref, y } = useParallax("14%");
  return (
    <section className="relative flex min-h-screen items-center overflow-hidden bg-navy pb-20 pt-32 sm:pt-36" ref={ref}>
      <div className="absolute inset-0 blueprint-grid" aria-hidden="true" />
      <div
        className="absolute -left-40 top-1/3 h-[560px] w-[560px] rounded-full bg-gold/[0.09] blur-[150px]"
        aria-hidden="true"
      />
      <div className="container-x relative grid items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="eyebrow-on-dark flex items-center gap-3">
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-gold" aria-hidden="true" />
            Legal Advisory · India
          </p>
          <MaskedLines
            className="mt-7 text-[2.6rem] font-medium leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-[4.4rem]"
            lineClass="pb-[0.06em]"
            delay={0.25}
            lines={[
              "Strategic Legal",
              "Solutions for",
              <span key="l3">
                <em className="not-italic text-gold">Businesses</em> and
              </span>,
              "Individuals.",
            ]}
          />
          <Reveal delay={0.9} y={20}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg" data-testid="hero-subheading">
              Practical legal guidance across corporate and commercial law,
              intellectual property, contracts, real estate, and regulatory
              compliance.
            </p>
          </Reveal>
          <Reveal delay={1.05} y={20}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link to="/contact" data-testid="hero-consultation-cta-button" className="btn-gold">
                Submit a Legal Enquiry <ArrowRight size={16} />
              </Link>
              <Link to="/services" data-testid="hero-explore-services-button" className="btn-outline-light">
                Explore Our Services
              </Link>
            </div>
          </Reveal>
          <Reveal delay={1.2} y={16}>
            <p className="mt-12 flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest2 text-white/40">
              <Phone size={12} className="text-gold" /> {SITE.phoneDisplay}
              <span className="h-px w-8 bg-gold/40" /> Confidential. Considered. Counselled.
            </p>
          </Reveal>
        </div>

        <div className="hidden lg:col-span-5 lg:block">
          <div className="relative ml-auto max-w-[440px]">
            <div className="absolute -left-6 -top-6 h-24 w-24 border-l-2 border-t-2 border-gold/70" aria-hidden="true" />
            <div className="absolute -bottom-6 -right-6 h-24 w-24 border-b-2 border-r-2 border-gold/70" aria-hidden="true" />
            <motion.div style={{ y }} className="relative overflow-hidden">
              <img
                src={IMAGES.hero}
                alt="Modern corporate architecture in glass and steel"
                className="aspect-[4/5] w-full scale-[1.12] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/25 to-transparent" aria-hidden="true" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="font-mono text-[10px] uppercase tracking-widest2 text-gold">
                  Precision · Structure · Foresight
                </p>
              </div>
            </motion.div>
            <div className="absolute -left-16 top-14 w-60">
              <HeroCard />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Intro = () => {
  const imgRef = useRef(null);
  const inView = useInView(imgRef, { once: true, amount: 0.3 });
  return (
    <section className="bg-white py-24 sm:py-28 lg:py-32">
      <div className="container-x grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="The Practice"
            title="A modern legal practice, built for the way business is done in India."
          />
          <Reveal delay={0.15}>
            <p className="mt-6 text-base leading-relaxed text-steel">
              Upgrade Law advises businesses, startups, entrepreneurs and
              individuals across the full arc of commercial life — from
              incorporation and contracts to trademarks, property, compliance
              and disputes.
            </p>
            <p className="mt-4 text-base leading-relaxed text-steel">
              The approach is deliberately restrained: candid advice, precise
              documentation, and solutions you can actually operate. No theatre,
              no jargon — just legal work that holds up when it matters.
            </p>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-gold-dark"
            >
              More about the practice <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div ref={imgRef} className="relative">
              <div className="absolute -right-5 -top-5 h-full w-full border border-gold/50" aria-hidden="true" />
              <motion.img
                src={IMAGES.boardroom}
                alt="A sunlit corporate boardroom"
                initial={{ clipPath: "inset(0 100% 0 0)" }}
                animate={inView ? { clipPath: "inset(0 0% 0 0)" } : {}}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-[16/10] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const WhyBento = () => (
  <section className="relative overflow-hidden bg-navy py-24 sm:py-28 lg:py-32">
    <div className="absolute inset-0 blueprint-grid" aria-hidden="true" />
    <div className="container-x relative">
      <SectionHeading
        light
        eyebrow="Why Upgrade Law"
        title="Four commitments that shape every engagement."
        description="These are not slogans — they are the operating rules of the practice."
      />
      <Stagger className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-12">
        {PILLARS.map((p, i) => (
          <StaggerItem
            key={p.title}
            className={i === 0 ? "lg:col-span-5" : i === 1 ? "lg:col-span-7" : i === 2 ? "lg:col-span-7" : "lg:col-span-5"}
          >
            <div className="group h-full border border-white/12 bg-white/[0.04] p-9 transition-all duration-500 hover:border-gold/50 hover:bg-white/[0.07]">
              <div className="flex items-center justify-between">
                <p.icon size={26} strokeWidth={1.5} className="text-gold" />
                <span className="font-mono text-[11px] text-white/25">0{i + 1}</span>
              </div>
              <h3 className="mt-8 font-serif text-2xl text-white sm:text-3xl">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55">{p.d}</p>
            </div>
          </StaggerItem>
        ))}
        <StaggerItem className="lg:col-span-12">
          <div className="grid gap-8 border border-gold/30 bg-gradient-to-r from-gold/[0.08] to-transparent p-9 sm:grid-cols-3 sm:p-12">
            {[
              ["Understand", "We start by understanding the business, the transaction and the risk — not just the question asked."],
              ["Advise candidly", "You get a frank view of options, consequences and cost — in writing, in plain language."],
              ["Execute precisely", "Documentation, filings and follow-through, handled with care and tracked to completion."],
            ].map(([t, d], i) => (
              <div key={t}>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Step 0{i + 1}</p>
                <h3 className="mt-3 font-serif text-xl text-white sm:text-2xl">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{d}</p>
              </div>
            ))}
          </div>
        </StaggerItem>
      </Stagger>
    </div>
  </section>
);

const HomeIndustries = () => (
  <section className="bg-white py-24 sm:py-28 lg:py-32">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionHeading
          eyebrow="Industries We Serve"
          title="Grounded in the sectors that build India's economy."
        />
        <Reveal delay={0.15}>
          <Link to="/industries" className="btn-outline shrink-0">
            View all industries <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.num} delay={i * 0.04} y={18}>
            <Link
              to="/industries"
              data-testid={`industry-row-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 border-t border-navy/10 py-6 transition-colors last:border-b hover:bg-canvas sm:gap-8 sm:py-7"
            >
              <span className="font-mono text-xs text-gold-dark">{ind.num}</span>
              <div>
                <h3 className="font-serif text-xl text-ink transition-all duration-500 group-hover:translate-x-2 sm:text-2xl">
                  {ind.name}
                </h3>
                <p className="mt-1 hidden max-w-3xl text-sm text-steel sm:block">{ind.tagline}</p>
              </div>
              <ArrowUpRight
                size={18}
                className="text-steel/50 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold-dark"
              />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const InsightsPreview = () => {
  const { data: articles = [] } = useQuery({
    queryKey: ["articles"],
    queryFn: () => fetchArticles(),
  });
  return (
    <section className="bg-canvas py-24 sm:py-28 lg:py-32">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionHeading
            eyebrow="Legal Resources & Insights"
            title="Notes from the practice, written to be useful."
          />
          <Reveal delay={0.15}>
            <Link to="/insights" className="btn-outline shrink-0">
              All insights <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {articles.slice(0, 3).map((a, i) => (
            <ArticleCard key={a.slug} article={a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => (
  <section className="relative overflow-hidden bg-navy-dark py-24 sm:py-28">
    <div
      className="absolute left-1/2 top-1/2 h-[480px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.07] blur-[120px]"
      aria-hidden="true"
    />
    <div className="container-x relative text-center">
      <Reveal>
        <p className="eyebrow-on-dark">Begin a conversation</p>
        <h2 className="mx-auto mt-6 max-w-3xl text-3xl font-medium leading-[1.15] tracking-tight text-white sm:text-5xl">
          Tell us what you are facing.
          <span className="block italic text-gold">We will tell you where you stand.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/55">
          Send a brief description of your legal need through the enquiry form.
          Do not share confidential documents at this stage.
        </p>
        <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
          <Link to="/contact" data-testid="home-cta-enquiry-button" className="btn-gold">
            Submit a Legal Enquiry <ArrowRight size={16} />
          </Link>
          <a href={telLink} data-testid="home-cta-call-button" className="btn-outline-light">
            Call {SITE.phoneDisplay}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Home = () => (
  <>
    <Seo
      title="Strategic Legal Solutions for Businesses and Individuals"
      description="Upgrade Law — practical legal guidance across corporate and commercial law, intellectual property, contracts, real estate and regulatory compliance in India."
      path="/"
      jsonLd={legalServiceSchema()}
    />
    <Hero />
    <Marquee />
    <Intro />
    <section className="bg-cream py-24 sm:py-28 lg:py-32">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionHeading
            eyebrow="Legal Services"
            title="Six practice areas, one standard of care."
          />
          <Reveal delay={0.15}>
            <Link to="/services" className="btn-outline shrink-0">
              All services <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14">
          {SERVICES.map((s, i) => (
            <ServiceRow key={s.slug} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
    <WhyBento />
    <HomeIndustries />
    <InsightsPreview />
    <CtaBand />
  </>
);

export default Home;
